/**
 * Voice Changer mini popout window controller.
 *
 * Architecture:
 *   - createVoicePopout(opts)  — main controller; returns { toggle, stop, isActive }
 *   - createDomRenderer(...)   — renders state into HTML DOM (Document PiP window)
 *   - createCanvasRenderer(...)— renders state into 2D canvas (video PiP fallback)
 *
 *   Both renderers share a common interface: { render(state) }, where state is:
 *     { presetIcon, presetName, isLive, level, pitch, formant }
 *
 * Strategy chain in toggle():
 *   1. Document Picture-in-Picture API  (Chromium 116+) — real interactive HTML window
 *   2. Video PiP via canvas captureStream — Firefox / Safari / older Chromium
 *   3. None supported — caller decides UX via onUnsupported()
 *
 * Usage:
 *   var popout = createVoicePopout({
 *     templateId: 'vcPopoutTpl',      // <template> id for DOM layout
 *     cssText: '...',                  // CSS injected inline into PiP window (no FOUC)
 *     button: <HTMLElement>,
 *     strings: { title, openLabel, stopLabel, live, off, pitch, formant, lang, dir },
 *     getState: function(){ return {...state} },
 *     onTelemetry: function({strategy, ok, err}){},
 *     onUnsupported: function(){},
 *     onStop: function(){}
 *   });
 *   button.onclick = function(){ popout.toggle(); };
 */

function createDomRenderer(doc, templateId, strings, onStop) {
    var tpl = document.getElementById(templateId);
    if (!tpl) return null;
    doc.body.appendChild(tpl.content.cloneNode(true));

    var nodes = {
        icon: doc.querySelector('[data-pip=icon]'),
        name: doc.querySelector('[data-pip=name]'),
        status: doc.querySelector('[data-pip=status]'),
        statusText: doc.querySelector('[data-pip=statusText]'),
        fill: doc.querySelector('[data-pip=fill]'),
        pitch: doc.querySelector('[data-pip=pitch]'),
        formant: doc.querySelector('[data-pip=formant]'),
        stop: doc.querySelector('[data-pip=stop]')
    };
    nodes.stop.textContent = strings.stopLabel;
    nodes.stop.onclick = onStop;

    // Game mode badge
    var gameBadge = doc.createElement('div');
    gameBadge.className = 'game-badge';
    gameBadge.textContent = '🎮 GAME';
    gameBadge.style.cssText = 'display:none;text-align:center;font-size:12px;font-weight:700;color:#22c55e;background:rgba(34,197,94,.12);border:1px solid rgba(34,197,94,.25);border-radius:6px;padding:2px 8px;margin-top:4px';
    var wrap = doc.querySelector('.wrap');
    if (wrap) wrap.appendChild(gameBadge);

    return {
        render: function(s) {
            nodes.icon.textContent = s.presetIcon;
            nodes.name.textContent = s.presetName;
            nodes.status.className = s.isLive ? 'status' : 'status off';
            nodes.statusText.textContent = s.isLive ? strings.live : strings.off;
            nodes.fill.style.width = (s.level * 100) + '%';
            nodes.fill.style.background = s.level < 0.5 ? '#22c55e' : s.level < 0.8 ? '#eab308' : '#ef4444';
            nodes.pitch.textContent = strings.pitch + ': ' + (s.pitch > 0 ? '+' : '') + s.pitch + ' st';
            nodes.formant.textContent = strings.formant + ': ' + s.formant.toFixed(2) + '×';
            gameBadge.style.display = s.gameMode ? 'block' : 'none';
        }
    };
}

function createCanvasRenderer(canvas, strings) {
    var ctx = canvas.getContext('2d');
    return {
        render: function(s) {
            var W = canvas.width, H = canvas.height;
            ctx.fillStyle = '#0f172a';
            ctx.fillRect(0, 0, W, H);

            ctx.fillStyle = '#38bdf8';
            ctx.font = 'bold 28px system-ui';
            ctx.textAlign = 'center';
            ctx.fillText(s.presetIcon + ' ' + s.presetName, W / 2, 50);

            ctx.fillStyle = '#1e293b';
            ctx.fillRect(20, 70, W - 40, 20);
            ctx.fillStyle = s.level < 0.5 ? '#22c55e' : s.level < 0.8 ? '#eab308' : '#ef4444';
            ctx.fillRect(20, 70, (W - 40) * s.level, 20);

            ctx.font = '16px system-ui';
            ctx.fillStyle = '#94a3b8';
            ctx.textAlign = 'left';
            ctx.fillText(strings.pitch + ': ' + (s.pitch > 0 ? '+' : '') + s.pitch + ' st', 20, 120);
            ctx.textAlign = 'right';
            ctx.fillText(strings.formant + ': ' + s.formant.toFixed(2) + '×', W - 20, 120);

            ctx.font = '14px system-ui';
            ctx.textAlign = 'center';
            ctx.fillStyle = s.isLive ? '#22c55e' : '#ef4444';
            var statusLabel = s.isLive ? strings.live : strings.off;
            if (s.gameMode) statusLabel += ' 🎮';
            ctx.fillText('● ' + statusLabel, W / 2, 155);

            ctx.font = '12px system-ui';
            ctx.fillStyle = '#475569';
            ctx.fillText('timbrica.com/voice-changer', W / 2, 175);
        }
    };
}

window.createVoicePopout = function(opts) {
    // Delegates the PiP shell (capability detect, DocPiP/video-PiP/rAF/
    // lifecycle/telemetry/button-label) to the shared createPipPanel
    // (public/js/pip-panel.js). This file now owns ONLY the voice-specific
    // renderers above. Behaviour is identical to the former inline shell.
    if (typeof window.createPipPanel !== 'function') {
        // Shell not loaded — fail soft to the unsupported path so the page
        // doesn't throw. (pip-panel.js must be loaded before this file.)
        return {
            toggle: function () { if (opts.onUnsupported) opts.onUnsupported(); },
            stop: function () {},
            isActive: function () { return false; }
        };
    }
    return window.createPipPanel({
        width: 320,
        height: 240,
        title: (opts.strings && opts.strings.title) || 'Voice Changer',
        cssText: opts.cssText,
        strings: opts.strings || {},
        button: opts.button,
        getState: opts.getState,
        onTelemetry: opts.onTelemetry,
        onUnsupported: opts.onUnsupported,
        onStop: opts.onStop,
        createDomRenderer: function (doc, onStop) {
            return createDomRenderer(doc, opts.templateId, opts.strings, onStop);
        },
        createCanvasRenderer: function (canvas) {
            return createCanvasRenderer(canvas, opts.strings);
        }
    });
};
